import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Stack, Typography } from '@mui/material'

import { Button, Card, Skeleton } from '@repo/ui'
// Direct file imports, NOT the `components` barrel. Consistent with `ProductSelectField`'s
// own note: reaching back through a barrel that re-exports this tree is how the cycles in
// `ActionLine` / `ScanTargetButton` were introduced.
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

import { CompanyPicker, type PickableCompany } from './CompanyPicker'

/** The company rows the picker lists. Whatever `CompanyPicker` draws, plus the id. */
export type BrandContextCompany = PickableCompany

/**
 * The resolved half — everything the SERVER worked out for this company. Shapes
 * `GET /internal/labs/companies/:id/context`; see that endpoint for where each
 * value is read from and why the two hosts are not one host.
 */
export interface BrandContext {
  id: string
  name: string
  subdomain: string | null
  logo: string | null
  logoDark: string | null
  hidePoweredBy: boolean
  emailLinkHost: string
  emailLinkHostSource: 'verified_link_host' | 'web_app_url'
  certificateHost: string
  certificateHostSource:
    | 'primary_installation'
    | 'vera_hosted_installation'
    | 'web_app_url'
    | 'unresolved'
  installationCount: number
  /**
   * This company's catalogue, capped server-side, in `ProductPreview`'s own shape.
   *
   * It rides along with the context rather than being a second request because it
   * has exactly the same cache key — the company — and splitting one company change
   * into two round trips buys nothing.
   */
  products: Array<{
    id: string
    title: string | null
    sku: string | null
    images: string[]
  }>
}

interface BrandContextCardProps {
  /** Options for the picker. */
  companies: BrandContextCompany[]
  /** The picked company, or null before one is picked. */
  value: BrandContextCompany | null
  onChange: (company: BrandContextCompany | null) => void
  /** The server's answer for `value`. `null` while it is in flight or unresolved. */
  context: BrandContext | null
  loading?: boolean
  /** Where "Update installation settings" goes. See the docblock's routing note. */
  installationHref?: string
  /**
   * ⚠️ **OFF here on purpose, and it must stay off on every `/internal/*` surface.**
   *
   * The upgrade CTA is built (below) but not shown in Labs, because Labs is Vera
   * STAFF tooling: offering a Vera employee an upgrade path for a brand's plan is
   * nonsense, and worse, it is nonsense that looks like a real control. The CTA
   * belongs on the BRAND-facing surface — the Studio Email tab — where the person
   * reading it is the person who can actually buy the thing.
   *
   * The capability is built rather than deferred so that adopting this card on
   * Studio is a prop flip, not a second implementation of the same card (§36).
   */
  showUpgradeCta?: boolean
  onUpgradeClick?: () => void
}

/** Human wording for the server's source enums. One line, no jargon, no repo terms. */
const EMAIL_HOST_SOURCE_LABEL: Record<BrandContext['emailLinkHostSource'], string> = {
  verified_link_host: 'This brand’s verified email domain',
  web_app_url: 'Vera default. No verified email domain yet',
}

const CERTIFICATE_HOST_SOURCE_LABEL: Record<
  BrandContext['certificateHostSource'],
  string
> = {
  primary_installation: 'This brand’s primary install',
  vera_hosted_installation: 'The Vera-hosted install',
  web_app_url: 'The Vera app. No active install',
  unresolved: 'Could not be resolved',
}

/**
 * One resolved host: what it is, where it points, and which setting produced it.
 *
 * `overflowWrap: 'anywhere'` is load-bearing, not defensive. A host is a single
 * unbroken token (`https://louis-vuitton.app.dev.vera.inc` is 38 characters with no
 * space in it) and this card's content column is 262px, so without it the value does
 * not wrap — it pushes the panel wider than the rail that holds it.
 */
const HostRow = (props: { label: string; value: string; source: string }) => (
  <Stack spacing={1}>
    <Typography variant="body4" color="text.secondary">
      {props.label}
    </Typography>
    <Typography variant="body3" sx={{ overflowWrap: 'anywhere' }}>
      {props.value}
    </Typography>
    <Typography variant="body4" color="text.secondary">
      {props.source}
    </Typography>
  </Stack>
)

/**
 * The company a notification preview is rendered AGAINST, and the hosts that
 * company's links resolve to.
 *
 * ## What this replaced, and why a readout beats the two controls it removed
 *
 * The Notifications lab used to carry a free-text **Company name** field and a
 * **White label** switch. Measured before removing them: both fed exactly ONE
 * request (`/sms-preview`) and nothing else — the lab's own docblock said so in as
 * many words, *"They do NOT affect the email."* So the pair let you SIMULATE a
 * brand state that no real send could produce, on a surface whose entire purpose
 * is showing you what a real send looks like. A switch that composes a fictional
 * SMS prefix is not a preview of anything.
 *
 * Everything here is READ instead. The name comes from `companies`, the
 * white-label flag from `certificate_trigger_settings.hide_powered_by`, and the
 * two hosts from the same resolvers the send and scan paths call. Nothing on this
 * card is editable, which is the point: it tells you what this brand's mail will
 * actually say, and sends you to the surface that changes it.
 *
 * ## Two hosts, deliberately not one
 *
 * **Email links** is where a link INSIDE an email points (claim / accept / login).
 * **Certificate links** is where a certificate resolves a visitor to. A brand can
 * have two different answers — a verified email domain and a Shopify storefront,
 * say — and showing one under a label that implies both would be worse than
 * showing neither.
 *
 * ## Routing
 *
 * `installationHref` defaults to the BRAND's own Settings → Installation, which is
 * correct on a brand-facing surface. An internal surface previewing SOMEONE ELSE's
 * company must override it — sending Vera staff to their own installation settings
 * to change Louis Vuitton's would be a link that silently does the wrong thing.
 * Labs passes the internal company page for exactly that reason.
 *
 * §42: the link carries `useReferrerState()`, so wherever it lands can offer a way
 * back to the lab rather than stranding the reader on a settings page.
 */
export const BrandContextCard = (props: BrandContextCardProps) => {
  const {
    companies,
    value,
    onChange,
    context,
    loading = false,
    installationHref = ROUTES.SETTINGS_COMPANY_INSTALLATION,
    showUpgradeCta = false,
    onUpgradeClick,
  } = props

  const referrer = useReferrerState()
  const navigate = useNavigate()

  return (
    <Card title="Company" spacing={4}>
      {/* ONE control in both states, rather than the search-box/selected-card SWAP this
          replaced. The swap existed to stop the field reading as an unfinished search
          ("Louis") once a company was picked — a real problem with a raw search box, and
          one the shared `Autocomplete` does not have: it commits the pick INTO its own
          input, so the field reads "Louis Vuitton" and states the selection itself.

          Clearing moves with it — see `CompanyPicker`'s note on why it rides in the label
          row rather than as an adornment inside the field. It still hands back `null`,
          which returns the whole card to its unbranded state. */}
      <CompanyPicker companies={companies} value={value} onChange={onChange} />

      {value && (
        <>
          {/* §19 — the skeleton occupies the two host rows it stands in for, so the
              card does not grow by ~96px when the request lands. The heights are the
              rendered ones: a 3-line HostRow is 52px, the pair plus their 12px gap is
              116, and the note below them is 16. */}
          {loading || !context ? (
            <Stack spacing={3}>
              <Skeleton variant="rounded" width="100%" height={52} />
              <Skeleton variant="rounded" width="100%" height={52} />
              <Skeleton variant="rounded" width="70%" height={16} />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <HostRow
                label="Email links"
                value={context.emailLinkHost}
                source={EMAIL_HOST_SOURCE_LABEL[context.emailLinkHostSource]}
              />
              <HostRow
                label="Certificate links"
                value={context.certificateHost}
                source={
                  CERTIFICATE_HOST_SOURCE_LABEL[context.certificateHostSource]
                }
              />
              {/* The answer to "where do these come from", stated once rather than
                  repeated per row. Two sentences, not an em-dash-joined clause (§15). */}
              <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                These come from this brand’s installation settings. They are read
                here, not set here.
              </Typography>
            </Stack>
          )}

          {/* §60 — default size (40px). A card's own action is not a caption.

              `href` + an intercepted click, NOT `component={RouterLink}`. Both give
              client-side navigation, but only this one is TYPED: `@repo/ui`'s `ButtonProps`
              declares `href`/`target`/`rel` explicitly and deliberately (see its own
              docblock on why those were added), while `component`/`to`/`state` are not on
              it at all — MUI's `ButtonProps` pins `component` to `'button'` by default. So
              the RouterLink form compiles only by accident or by an `as any`, which §47
              calls out as routing around the shared component instead of improving it.

              The element is still a real anchor, so cmd-click and open-in-new-tab behave.
              The handler only takes over the PLAIN left click — the one case that can carry
              `state` (§42's referrer) through the router. A modified click falls through to
              the browser, which is correct: a new tab has no history to go back to. */}
          <Stack spacing={3}>
            <Button
              variant="secondary"
              href={installationHref}
              onClick={event => {
                if (
                  event.defaultPrevented ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey ||
                  event.button !== 0
                ) {
                  return
                }
                event.preventDefault()
                navigate(installationHref, { state: referrer })
              }}
            >
              Update installation settings
            </Button>
            {showUpgradeCta && (
              <Button variant="primary" onClick={onUpgradeClick}>
                Upgrade for full white label
              </Button>
            )}
          </Stack>
        </>
      )}
    </Card>
  )
}

/** Footprint-parity placeholder for the whole card (§19), used by the lab's own skeleton. */
export const BRAND_CONTEXT_CARD_H = 268
